import { Injectable } from '@angular/core';
import { LoggerService } from '@fantaz/shared/utils/log';
import { select, Store } from '@ngrx/store';

import { toSignal } from '@angular/core/rxjs-interop';
import {
  BuyPassRequest,
  TournamentPass,
} from '@fantaz/shared/data-access/models';
import { combineLatest, filter, map, Observable } from 'rxjs';
import * as PassesActions from './passes.actions';
import * as PassesSelectors from './passes.selectors';

@Injectable({
  providedIn: 'root',
})
export class PassesFacade {
  /**
   * Combine pieces of state using createSelector,
   * and expose them as observables through the facade.
   */
  loaded$ = this.store.pipe(select(PassesSelectors.getPassesLoaded));
  passes$ = this.store.pipe(select(PassesSelectors.getAllPasses));
  userPass$ = this.store.pipe(select(PassesSelectors.getActivePass));
  activeCount$ = this.store.pipe(select(PassesSelectors.getActiveCount));
  passesCanUsage$ = this.store.pipe(select(PassesSelectors.getPassesUsage));
  isBuyingTournamentPass = toSignal(
    this.store.pipe(select(PassesSelectors.selectIsBuyingTournamentPass)),
  );

  /**
   * TODO: Move to selector
   * Active Pass for tournament, it is a combination of if user have a pass
   * or if exist a pass with price 0.00 or entries is 0, the priority is as
   * follow:
   * 1. User pass
   * 2. Pass with price 0.00
   * 3. Pass with entries 0
   */
  activePass$: Observable<TournamentPass | undefined> = combineLatest([
    this.passes$.pipe(
      map((passes) =>
        passes.filter((pass) => {
          // ignore pass if not can be used or is redeemed
          const isRedeemed = pass?.price.promoCodes?.find(
            (e) => e.redeemDate && e.canBeUsed === false,
          );
          return isRedeemed == undefined && !pass.purchaseLimitReached;
        }),
      ),
    ),
    this.userPass$.pipe(
      map((pass) => {
        // ignore pass if not can be used or is redeemed
        const isRedeemed = pass?.price.promoCodes?.find(
          (e) => e.redeemDate && e.canBeUsed === false,
        );

        if (!isRedeemed) {
          return pass;
        }

        return undefined;
      }),
    ), // user passes
  ]).pipe(
    filter(([passes]) => passes.length > 0),
    map(([passes, userPass]) => {
      if (userPass) {
        // check if user pass is unlimited
        return userPass;
      }

      if (passes) {
        // find pass with price 0.00
        const freePass = passes.find((pass) => Number(pass.price.price) == 0);

        if (freePass) {
          return freePass;
        }

        // find pass with one entry
        const oneEntryPass = passes.find((pass) => pass.entries == 1);

        if (oneEntryPass) {
          return oneEntryPass;
        }
      }

      return undefined;
    }),
  );

  constructor(
    private readonly store: Store,
    private readonly logger: LoggerService,
  ) {}

  /**
   * Load the passes for the given event prototype.
   *
   * @param {object} options
   * @param {string} options.eventPrototypeUuid
   * @param {string} options.eventScheduleUuid
   */
  load(options: {
    eventPrototypeUuid: string;
    eventScheduleUuid: string;
    userId?: string;
  }) {
    this.logger.log('PassesFacade.load()');
    this.store.dispatch(
      PassesActions.loadTournamentPasses({
        eventPrototypeUuid: options.eventPrototypeUuid,
        eventScheduleUuid: options.eventScheduleUuid,
        userId: options.userId,
      }),
    );
  }

  buyPass(options: BuyPassRequest, args?: { playOnBuy: any }) {
    this.store.dispatch(
      PassesActions.buyTournamentPass({
        eventPrototypeUuid: options.eventPrototypeUuid,
        tournamentName: options.tournamentName,
        gameName: options.gameName,
        fantazPassUuid: options.fantazPassUuid,
        eventScheduleUuid: options.eventScheduleUuid,
        promoCodes: options.promoCodes,
        price: options.price,
        playOnBuy: args?.playOnBuy,
        passIndex: options.passIndex,
      }),
    );
  }
}
